import React from "react";
import get from "lodash.get";
import { createClasses } from "@bloomberg/media-ui-core";
import type { Position } from "@foundation/watchlist-client";
import styles from "./styles.module.css";
import {
  formatBigNumber,
  formatPercentChange,
  formatPriceChange,
  isTickerUp,
} from "~utils/portfolio/tickerUtils";
import { PortfolioColumnConfig, TickerUrlFn } from "~types/portfolio";
import { ENTRY_DATA_FORMAT } from "~constants/Portfolio";
import { formatWithCommasAndDecimals } from "~utils/portfolio/formatNumber";
import timeFormat from "~utils/timeFormat";
import BowlingBallChart from "~components/Portfolio/BowlingBallChart";
import { Locale } from "~constants/i18n";

type TickerEntryProps = PortfolioColumnConfig & {
  ticker: Position;
};

const getValueFromTicker = (
  ticker: Position,
  lodashPath: string,
  isPrimaryInformation: boolean
): string | number => {
  const defaultValue = isPrimaryInformation ? "—" : "";
  const value = get(ticker, lodashPath, null);

  return value === null ? defaultValue : value;
};

// Only works for words that can be made plural by adding the letter 's', in this case "lot" and "share"
const makePlural = (str: string, count: number) => {
  if (count === 1) {
    return str;
  }

  return `${str}s`;
};

const formatData = (
  data: string | number,
  formatType: string,
  columnName: string,
  securityType: string
) => {
  const { PRICE_CHANGE, PERCENT_CHANGE, COMMAS_AND_DECIMALS, BIG_NUMBER } =
    ENTRY_DATA_FORMAT;

  if (data === "—" || typeof data === "string") {
    return data;
  }

  // Used to overwrite defined formatTypes for currencies, which happen to be in this case BIG_NUMBER
  const adjustedFormatType =
    formatType === BIG_NUMBER && securityType === "CUR"
      ? COMMAS_AND_DECIMALS
      : formatType;

  switch (adjustedFormatType) {
    case PRICE_CHANGE:
      return formatPriceChange(data, securityType);
    case PERCENT_CHANGE:
      return formatPercentChange(data);
    case COMMAS_AND_DECIMALS:
      if (columnName === "VOLUME") {
        return formatWithCommasAndDecimals(data, 0);
      }

      if (securityType === "CUR") {
        return formatWithCommasAndDecimals(data, 4);
      }

      return formatWithCommasAndDecimals(data);
    case BIG_NUMBER:
      return formatBigNumber(data, 2);
    default:
      return data;
  }
};

const chooseColor = (change: number) => {
  return change >= 0 ? styles.positive : styles.negative;
};

const generatePrimaryLabel = (
  tickerPrimaryURL?: TickerUrlFn,
  primaryInformation = ""
) => {
  return tickerPrimaryURL ? (
    <a
      className={styles.primaryLink}
      href={tickerPrimaryURL(primaryInformation)}
      target="_blank"
    >
      {primaryInformation}
    </a>
  ) : (
    primaryInformation
  );
};

export default function TickerEntry(props: TickerEntryProps) {
  const {
    tickerPrimaryURL,
    tickerPrimaryAriaLabel,
    tickerSecondaryAriaLabel,
    alignment,
    columnName,
  } = props;

  const generateTickerConfig = () => {
    const {
      ticker,
      tickerPrimaryLodashPath,
      tickerSecondaryLodashPath,
      primaryDataFormat,
      secondaryDataFormat,
      tickerTertiaryLodashPath,
    } = props;

    const tickerConfig = {
      primaryInformation: getValueFromTicker(
        ticker,
        tickerPrimaryLodashPath,
        true
      ),
      secondaryInformation: getValueFromTicker(
        ticker,
        tickerSecondaryLodashPath,
        false
      ),
      tertiaryInformation: getValueFromTicker(
        ticker,
        tickerTertiaryLodashPath,
        false
      ),
      securityType: getValueFromTicker(ticker, "security.SECTYPE", true),
      totalGainLoss: 0,
      shares: 0,
      performanceIndicator: "",
    };

    // A one-off market bar indicator
    if (columnName === "SYMBOL") {
      tickerConfig.totalGainLoss = getValueFromTicker(
        ticker,
        "marketValue.totalGainLoss.value",
        true
      ) as number;
      tickerConfig.shares = getValueFromTicker(
        ticker,
        "shares.number",
        false
      ) as number;
    }

    // A one-off for the count of lots for a given ticker
    if (columnName === "LOTS") {
      const lotCount = (tickerConfig.primaryInformation as string).length;
      const shareCount = tickerConfig.secondaryInformation;

      tickerConfig.primaryInformation = `${lotCount} ${makePlural(
        "Lot",
        lotCount
      )}`;
      tickerConfig.secondaryInformation = `${shareCount} ${makePlural(
        "Share",
        shareCount as number
      )}`;
    }

    // A one-off for the last update time of a price
    if (columnName === "PRICE") {
      tickerConfig.secondaryInformation =
        tickerConfig.secondaryInformation &&
        timeFormat(tickerConfig.secondaryInformation, Locale.En, "HH:mm");
    }

    // A one-off for modifying background colors of the Gain columns to indicate performance
    if (columnName === "TOTAL GAIN" || columnName === "1D GAIN") {
      tickerConfig.performanceIndicator = chooseColor(
        tickerConfig.primaryInformation as number
      );
      tickerConfig.shares = getValueFromTicker(
        ticker,
        "shares.number",
        false
      ) as number;
    }

    tickerConfig.primaryInformation = formatData(
      tickerConfig.primaryInformation,
      primaryDataFormat,
      columnName,
      tickerConfig.securityType as string
    );
    tickerConfig.secondaryInformation = formatData(
      tickerConfig.secondaryInformation,
      secondaryDataFormat,
      columnName,
      tickerConfig.securityType as string
    );

    return tickerConfig;
  };

  const generateCellContent = (
    primaryInformation: string,
    secondaryInformation: string,
    performanceIndicator: string,
    shares: number
  ) => {
    const rightAlignment = alignment === "right" ? styles.right : "";
    const primaryAriaLabel =
      primaryInformation === "—" ? "No data" : tickerPrimaryAriaLabel;
    const gainColor =
      primaryInformation === "—" || shares === 0 ? "" : performanceIndicator;

    return (
      <div className={styles.tickerEntryWrapper}>
        <div
          className={`${styles.primaryInformation} ${gainColor} ${rightAlignment}`}
          aria-label={primaryAriaLabel}
          data-testid={`primaryInfo-${primaryInformation}`}
        >
          {generatePrimaryLabel(tickerPrimaryURL, primaryInformation)}
        </div>
        <div
          className={`${styles.secondaryInformation} ${rightAlignment}`}
          aria-label={tickerSecondaryAriaLabel}
          data-testid={`secondaryInfo-${secondaryInformation}`}
        >
          {secondaryInformation}
        </div>
      </div>
    );
  };

  const {
    primaryInformation,
    secondaryInformation,
    tertiaryInformation,
    totalGainLoss,
    performanceIndicator,
    shares,
    securityType,
  } = generateTickerConfig();

  const marketPerformance = isTickerUp(totalGainLoss)
    ? styles.marketsUp
    : styles.marketsDown;
  const totalPerformance = shares ? marketPerformance : "";
  const rangeCellContent =
    primaryInformation && secondaryInformation && tertiaryInformation ? (
      <div
        className={createClasses([
          styles.tickerEntryWrapper,
          styles.bowlingBallGraph,
        ])}
      >
        <BowlingBallChart
          width={100}
          currentValue={tertiaryInformation as number}
          startValue={secondaryInformation as number}
          endValue={primaryInformation as number}
          tickerType={securityType as string}
          variation="stackedText"
        />
      </div>
    ) : (
      generateCellContent("—", "", performanceIndicator, shares)
    );

  const cellContent =
    columnName === "DAY RANGE" || columnName === "52WK RANGE"
      ? rangeCellContent
      : generateCellContent(
          primaryInformation as string,
          secondaryInformation as string,
          performanceIndicator,
          shares
        );

  const cx = createClasses([styles.tickerEntry, totalPerformance]);

  return (
    <div className={cx} role="cell">
      {cellContent}
    </div>
  );
}
